import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useApi } from '../../contexts/ApiContext'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import ChatButton from '../../components/ChatButton'
import { trackClaimDetailsView, openChatWithContext } from '../../utils/eventTracking'
import { 
  ArrowLeft, 
  FileText, 
  Calendar, 
  DollarSign, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Building2, 
  User, 
  CreditCard,
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  MessageSquare,
  Download
} from 'lucide-react'

const ClaimDetails = () => {
  const { claimId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getUserPoliciesWithClaims } = useApi()
  const [claim, setClaim] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const userId = searchParams.get('user_id')

  useEffect(() => {
    if (claimId && userId) {
      loadClaimDetails()
    } else {
      setError('Missing claim ID or user ID')
      setLoading(false)
    }
  }, [claimId, userId])

  const loadClaimDetails = async () => {
    try {
      setLoading(true)
      console.log('Loading claim details for claim ID:', claimId, 'user ID:', userId)
      
      // Get all claims for the user and find the specific one
      const policiesWithClaimsData = await getUserPoliciesWithClaims(userId)
      const specificClaim = policiesWithClaimsData.find(item => 
        item.claim_id === claimId && item.claim_id !== null
      )
      
      if (specificClaim) {
        setClaim(specificClaim)
        
        // Track claim details view
        trackClaimDetailsView(userId, claimId, {
          claim_status: specificClaim.claim_status,
          claim_amount: specificClaim.claim_amount,
          policy_id: specificClaim.policy_id
        })
      } else {
        setError('Claim not found')
      }
    } catch (err) {
      console.error('Error loading claim details:', err)
      setError('Failed to load claim details')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-emerald-100 text-emerald-800 border-2 border-emerald-300'
      case 'APPROVED': return 'bg-green-100 text-green-800'
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'REJECTED': return 'bg-red-100 text-red-800'
      case 'UNDER_REVIEW': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="h-5 w-5" />
      case 'APPROVED': return <CheckCircle className="h-5 w-5" />
      case 'PENDING': return <Clock className="h-5 w-5" />
      case 'REJECTED': return <AlertCircle className="h-5 w-5" />
      case 'UNDER_REVIEW': return <Clock className="h-5 w-5" />
      default: return <Clock className="h-5 w-5" />
    }
  }

  const handleBackClick = () => {
    navigate(`/customer/claims?user_id=${userId}`)
  }

  const handleDownloadDocuments = async () => {
    try {
      // Fetch documents related to this claim
      const { data: claimDocuments, error } = await supabase
        .from('documents')
        .select('*')
        .eq('claim_id', claimId)

      if (error) {
        console.error('Error fetching claim documents:', error)
        toast.error('Failed to fetch documents')
        return
      }

      if (!claimDocuments || claimDocuments.length === 0) {
        toast.error('No documents attached to this claim')
        return
      }

      // Open each document in a new tab
      let downloadedCount = 0
      claimDocuments.forEach((doc, index) => {
        if (doc.file_url) {
          setTimeout(() => {
            window.open(doc.file_url, '_blank')
          }, index * 500) // Stagger downloads to avoid popup blocker
          downloadedCount++
        }
      })

      if (downloadedCount > 0) {
        toast.success(`Opening ${downloadedCount} document(s)`)
      } else {
        toast.error('No downloadable files found for this claim')
      }
    } catch (error) {
      console.error('Error downloading documents:', error)
      toast.error('Failed to download documents')
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Claim Details</h1>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading claim details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Claim Details</h1>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Claim</h3>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    )
  }

  if (!claim) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Claim Details</h1>
        </div>
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Claim Not Found</h3>
          <p className="text-gray-500">The requested claim could not be found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Claim Details</h1>
            <p className="text-xs sm:text-sm text-gray-600">Claim #{claim.claim_number || 'N/A'}</p>
          </div>
        </div>
        <button
          onClick={() => {
            const claimNumber = claim.claim_number || claimId
            const claimAmount = claim.total_bill_amount ? `₹${claim.total_bill_amount.toLocaleString('en-IN')}` : 
                               claim.approved_amount ? `₹${claim.approved_amount.toLocaleString('en-IN')}` : 
                               claim.paid_amount ? `₹${claim.paid_amount.toLocaleString('en-IN')}` : 'N/A'
            const claimStatus = claim.claim_status || 'N/A'
            
            openChatWithContext(
              `I need help with my claim. Claim Number: ${claimNumber}, Status: ${claimStatus}, Amount: ${claimAmount}. I have a question about this claim.`,
              'claim_details_page'
            )
          }}
          className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm sm:text-base"
        >
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="font-medium hidden sm:inline">Chat about Claim</span>
          <span className="font-medium sm:hidden">Chat</span>
        </button>
      </div>

      {/* Status Badge */}
      <div className="flex justify-start">
        <span className={`px-4 py-2 text-sm font-bold rounded-full flex items-center space-x-2 ${getStatusColor(claim.claim_status)}`}>
          {getStatusIcon(claim.claim_status)}
          <span>{claim.claim_status}</span>
        </span>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Claim Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Claim Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Number</label>
                <p className="text-gray-900 font-semibold">{claim.claim_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Type</label>
                <p className="text-gray-900">{claim.claim_type || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Category</label>
                <p className="text-gray-900">{claim.claim_category || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Mode</label>
                <p className="text-gray-900">{claim.claim_mode || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Claim Stage</label>
                <p className="text-gray-900">{claim.claim_stage || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Processing Status</label>
                <p className="text-gray-900">{claim.processing_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Processing Priority</label>
                <p className="text-gray-900">{claim.processing_priority || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Incident Date</label>
                <p className="text-gray-900">
                  {claim.incident_date ? new Date(claim.incident_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created Date</label>
                <p className="text-gray-900">
                  {claim.created_at ? new Date(claim.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Financial Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Total Bill Amount</label>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{claim.total_bill_amount?.toLocaleString() || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Approved Amount</label>
                <p className="text-xl font-semibold text-green-600">
                  ₹{claim.approved_amount?.toLocaleString() || '0'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Paid Amount</label>
                <p className="text-xl font-semibold text-blue-600">
                  ₹{claim.paid_amount?.toLocaleString() || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Financial Breakdown */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Detailed Financial Breakdown
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Room Rent Amount</label>
                <p className="text-lg font-semibold text-gray-900">₹{claim.room_rent_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Medicine Amount</label>
                <p className="text-lg font-semibold text-gray-900">₹{claim.medicine_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Investigation Amount</label>
                <p className="text-lg font-semibold text-gray-900">₹{claim.investigation_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Surgery Amount</label>
                <p className="text-lg font-semibold text-gray-900">₹{claim.surgery_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Other Amount</label>
                <p className="text-lg font-semibold text-gray-900">₹{claim.other_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Sum Insured</label>
                <p className="text-lg font-semibold text-blue-600">₹{claim.sum_insured?.toLocaleString() || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Medical Information - Health Insurance */}
          {claim.claim_type && ['INPATIENT', 'OUTPATIENT', 'DAYCARE', 'EMERGENCY', 'PRE_APPROVAL'].includes(claim.claim_type) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User className="h-5 w-5 mr-2" />
                Medical Information
              </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Disease/Condition</label>
                <p className="text-gray-900">{claim.disease_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Disease Category</label>
                <p className="text-gray-900">{claim.category || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Hospital</label>
                <p className="text-gray-900">{claim.hospital_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Hospital Code</label>
                <p className="text-gray-900">{claim.hospital_code || 'Not available'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Hospital City</label>
                <p className="text-gray-900">{claim.hospital_city || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">ICD Code</label>
                <p className="text-gray-900">{claim.icd_code || 'Not available'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-500">Diagnosis Details</label>
                <p className="text-gray-900">{claim.diagnosis_details || 'N/A'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-500">Treatment Details</label>
                <p className="text-gray-900">{claim.treatment_details || 'N/A'}</p>
              </div>
            </div>
          </div>
          )}


          {/* Claim Processing Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Claim Processing
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Processing Status</label>
                <p className="text-gray-900">{claim.processing_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Processing Priority</label>
                <p className="text-gray-900">{claim.processing_priority || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Approval Status</label>
                <p className="text-gray-900">{claim.approval_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Payment Status</label>
                <p className="text-gray-900">{claim.payment_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Deficiency Status</label>
                <p className="text-gray-900">{claim.deficiency_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Compliance Status</label>
                <p className="text-gray-900">{claim.compliance_status || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Important Dates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created Date</label>
                <p className="text-gray-900">
                  {claim.created_at ? new Date(claim.created_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
                <p className="text-xs text-gray-500">
                  {claim.created_at ? new Date(claim.created_at).toLocaleString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }) : ''}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Updated Date</label>
                <p className="text-gray-900">
                  {claim.updated_at ? new Date(claim.updated_at).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
                <p className="text-xs text-gray-500">
                  {claim.updated_at ? new Date(claim.updated_at).toLocaleString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }) : ''}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Incident Date</label>
                <p className="text-gray-900">
                  {claim.incident_date ? new Date(claim.incident_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Admission Date</label>
                <p className="text-gray-900">
                  {claim.admission_date ? new Date(claim.admission_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Discharge Date</label>
                <p className="text-gray-900">
                  {claim.discharge_date ? new Date(claim.discharge_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Treatment Start</label>
                <p className="text-gray-900">
                  {claim.treatment_start_date ? new Date(claim.treatment_start_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Treatment End</label>
                <p className="text-gray-900">
                  {claim.treatment_end_date ? new Date(claim.treatment_end_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Approval Date</label>
                <p className="text-gray-900">
                  {claim.approval_date ? new Date(claim.approval_date).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Financial Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Detailed Financial Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Room Rent Amount</label>
                <p className="text-gray-900">₹{claim.room_rent_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Medicine Amount</label>
                <p className="text-gray-900">₹{claim.medicine_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Investigation Amount</label>
                <p className="text-gray-900">₹{claim.investigation_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Surgery Amount</label>
                <p className="text-gray-900">₹{claim.surgery_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Other Amount</label>
                <p className="text-gray-900">₹{claim.other_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Claimed Amount</label>
                <p className="text-gray-900">₹{claim.claimed_amount?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Sum Insured</label>
                <p className="text-gray-900">₹{claim.sum_insured?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Coverage Percentage</label>
                <p className="text-gray-900">{claim.coverage_percentage ? `${claim.coverage_percentage}%` : '0%'}</p>
              </div>
            </div>
          </div>

          {/* Medical Review & Investigation */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Medical Review & Investigation
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Medical Review Required</label>
                <p className="text-gray-900">{claim.medical_review_required ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Medical Review Status</label>
                <p className="text-gray-900">{claim.medical_review_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Investigation Required</label>
                <p className="text-gray-900">{claim.investigation_required ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Investigation Status</label>
                <p className="text-gray-900">{claim.investigation_status || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Fraud Suspicion</label>
                <p className="text-gray-900">{claim.fraud_suspicion_flag ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Customer Notified</label>
                <p className="text-gray-900">{claim.customer_notified ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>

          {/* Documents & Requirements */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Documents & Requirements
            </h3>
            <div className="space-y-4">
              {claim.required_documents && claim.required_documents.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Required Documents</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {claim.required_documents.map((doc, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {claim.submitted_documents && claim.submitted_documents.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Submitted Documents</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {claim.submitted_documents.map((doc, index) => (
                      <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {claim.missing_documents && claim.missing_documents.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Missing Documents</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {claim.missing_documents.map((doc, index) => (
                      <span key={index} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        {doc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Policy Information */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Policy Information
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Number</label>
                <p className="text-gray-900 font-semibold">{claim.policy_number || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Policy Name</label>
                <p className="text-gray-900">{claim.policy_name || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Sum Insured</label>
                <p className="text-gray-900">₹{claim.sum_insured?.toLocaleString() || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Quick Actions</h3>
            <button 
              onClick={handleDownloadDocuments}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download Documents</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClaimDetails
